<!-- eslint-disable vue/no-deprecated-slot-attribute -->
<template>
  <div class="nav-bar">
    <div
      v-touch:moved="touchStart"
      v-touch:moving="touchMove"
      v-touch:end="stopDrag"
      :class="['content', { dragging: isDragging }]"
      :style="wrapperStyle"
    >
      <tree-menu
        class="nav-bar__menu"
        :items="MENU"
        :max-depth="3"
      >
        <template #prepend>
          <client-only>
            <ul v-if="!adaptive?.isDesktop && nav?.navOpened">
              <mobile-city />
              <a href="/stores" class="link">
                <f-sprite-icon :icon-link="$options.icons.IconPoint" />
                Магазины
              </a>
              <mobile-search />
              <mobile-account />
              <mobile-manual-barcode-search />
            </ul>
          </client-only>
        </template>

        <template #append>
          <client-only>
            <banner
              v-if="!adaptive?.isDesktop"
              :data="mainBannerData"
              slot-name="burger_main"
            />

            <div v-if="!adaptive?.isDesktop" class="mobile-contacts">
              <a href="tel:+78007700101">
                8 800 770 01 01
              </a>
              <p>Мы принимаем звонки с 7:00 до 22:00 (мск).</p>
              <p>Звонок по России бесплатный.</p>
            </div>
          </client-only>
        </template>
      </tree-menu>
    </div>

    <transition name="fade">
      <div
        v-if="nav.navOpened"
        class="overlay"
        @click="nav.hide()"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, inject } from 'vue';
import { mapGetters } from 'vuex';

import { useBanner } from '@/composables/useBanner';
import { BANNER_SLOTS } from '@/constants/banner-slots';
import { IAdaptive } from '@/utils/system/adaptive';

import IconPoint from '../../assets/icons/point.svg';
import Banner from '../banner/banner.vue';

import treeMenu from './tree-menu.vue';

export default defineComponent({
  name: 'nav-bar',

  components: {
    Banner,
    treeMenu,
    MobileCity: defineAsyncComponent(() => import('./mobile-city.vue')),
    MobileSearch: defineAsyncComponent(() => import('./mobile-search.vue')),
    MobileAccount: defineAsyncComponent(() => import('./mobile-account.vue')),
    MobileManualBarcodeSearch: defineAsyncComponent(() => import('./mobile-manual-barcode-search.vue')),
  },

  icons: {
    IconPoint,
  },

  setup() {
    const adaptive = inject<IAdaptive>('adaptive');
    const banner = useBanner();
    const nav: any = inject('$nav');

    return {
      nav,
      adaptive,
      banner,
    };
  },

  data: () => ({
    isDragging: false,
    dragStartX: 0,
    dragStartY: 0,
    dragDiffX: 0,
  }),

  computed: {
    ...mapGetters({
      MENU: 'menu/mainMenu',
    }),

    wrapperStyle() {
      if ((typeof window === 'undefined') || this.adaptive?.isDesktop) {
        return {};
      }

      return {
        transform: `translateX(-${this.nav.navOpened ? this.dragDiffX : 320}px)`,
      };
    },

    mainBannerData() {
      return this.banner.getFirstBanner('burger_main');
    },
  },

  methods: {
    touchStart({ targetTouches }) {
      /*
      Для меню не очень актуальная фича,
      но нам нужен свой свайпер товаров/фото
      тк нет хорошего решения с snap-scroll
      нам надо будет его делать самим.
      */
      const [touch] = targetTouches;
      this.dragStartX = touch.clientX || 0;
      this.dragStartY = touch.clientY || 0;
    },

    touchMove({ targetTouches }) {
      /*
      Надо утащить математику направления
      у slick.js - она там идеальная
      */
      const [touch] = targetTouches;
      if (!this.isDragging && this.dragStartX - (touch.clientX || 0) > this.dragStartY - (touch.clientY || 0)) {
        return;
      }
      this.isDragging = true;
      this.dragDiffX = this.dragStartX - (touch.clientX || 0);
    },

    stopDrag() {
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      if (this.dragDiffX > 50) {
        this.nav.hide();
      }
      this.dragDiffX = 0;
    },
  },

  serverPrefetch() {
    return this.banner.loadBanners({
      slots: BANNER_SLOTS.menu,
      withCategory: false,
    });
  },
});
</script>

<style lang="scss">
.nav-bar {
  @include stacking("sidebar");
  position: relative;
  .content {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 320px;
    overflow: hidden;
    background: $white;
    transform: translateX(-320px);
    transition: transform .2s;

    &.dragging {
      transition: none;
    }
  }

  .root-categories > li {
    & > .link {
      padding: 12px 0;
    }
  }

  .overlay {
    @include stacking("behind");
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(51, 51, 51, .64);
  }

  .close {
    width: 24px;
    height: 24px;
    color: $gray_lu;
  }

  .mobile-contacts {
    margin-top: auto;
    padding: $containerGapMobile;

    a {
      margin: 4px 0;
      font-size: 16px;
      line-height: 20px;
      font-weight: normal;
    }

    p {
      color: $gray;
      font-size: 14px;
      line-height: 24px;
    }
  }
}

@include breakpoint("desktop") {
  .nav-bar {
    @include stacking("desktop-menu");
    border-top: 1px solid $gray_lu;

    &__menu {
      .link {
        font-size: 16px;
      }
    }

    .root-categories > li {
      & > .link {
        padding: 12px $containerGapMobile;
      }
    }

    .content {
      position: relative;
      top: auto;
      right: auto;
      left: auto;
      max-width: initial;
      overflow: visible;
      transform: none;
      transition: none;
    }
  }

  .mobile-contacts {
    display: none;
  }
}
</style>
